/**
 * Reminders' recipes as data (Mac recipes R3), from the research (apple/reminders.md). Each passes the
 * program lint and ships held until the Reminders drill lifts it. A new reminder runs through the
 * EventKit verb in the background; a named list is resolved against the live lists first. Dates wait
 * for code to parse them (the `when` kind), and any act on one reminder by its title waits for the
 * reminders.index resolver; both are on the R3 review lists.
 */
import { opening, recipeApp, type OsRecipe } from '../os-recipes.js';

const REMINDERS = recipeApp('com.apple.reminders');

/** A reversible menu press in the running app (R3c); `pair` is a Show/Hide title's opposite, so a press
 *  that finds the other title says it is likely already so. The reply names the item it matched. */
const menu = (id: string, title: string, description: string, path: readonly [string, string, ...string[]], cues: readonly string[], reply: string, pair?: string): OsRecipe => ({
  id, app: REMINDERS, status: 'held', title, description, params: {},
  steps: [{ op: 'activate' }, { op: 'menu', path, ...(pair ? { pair: [path.at(-1)!, pair] as const } : {}) }],
  tier: 'reversible', verify: 'echo', cues, reply,
});

export const RECIPES: readonly OsRecipe[] = [
  // ── New reminders ─────────────────────────────────────────────────────────────────────────────
  {
    id: 'reminders.add', app: REMINDERS, status: 'held',
    title: 'Add a reminder',
    description: 'Into the default list, in the background: Reminders never comes to the front. A list they name is the list recipe. For asks like "remind me to call mom" or "add buy milk to my reminders".',
    params: {
      title: { kind: 'literal', description: 'What to be reminded about, in their words', cut: ['after_saying', 'to_end'] },
    },
    steps: [{ op: 'verb', id: 'reminders.create', params: { title: '{title}' } }],
    tier: 'reversible', verify: 'echo', cues: ['remind', 'reminder', 'reminders', 'remember', 'add', 'put', 'put on my list', 'reminder daal', 'remind me to', 'remember to', 'add a reminder to'],
    reply: 'Added {title} to your reminders.',
  },
  {
    id: 'reminders.add_to_list', app: REMINDERS, status: 'held',
    title: 'Add a reminder to a named list',
    description: 'A list they name, matched against their live lists first. For asks like "add eggs to my groceries list" or "put call the vet on the family list".',
    params: {
      title: { kind: 'literal', description: "The reminder's words", cut: 'to_end' },
      list: { kind: 'span', description: "Words from the list's name" },
    },
    steps: [
      { op: 'resolve', source: 'reminders.list_lists', param: 'list', into: 'list_name' },
      { op: 'verb', id: 'reminders.create', params: { title: '{title}', list: '{list_name}' } },
    ],
    tier: 'reversible', verify: 'echo', cues: ['add', 'put', 'list'],
    reply: 'Added {title} to your {label} list.',
  },
  // ── Search and the selection ──────────────────────────────────────────────────────────────────
  // drill: the search field's AX label (labelIn), read through `selection` before this is lifted
  {
    id: 'reminders.search', app: REMINDERS, status: 'held', opens: true,
    title: 'Search reminders',
    description: 'Results filter as the words are typed; nothing is pressed after them. For asks like "search reminders for dentist" or "find the reminder about the visa".',
    params: {
      query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' },
    },
    steps: [
      { op: 'activate' },
      { op: 'focus_mark' },
      { op: 'key', chord: 'cmd+f' },
      { op: 'focus_moved', timeoutMs: 800, labelIn: ['search'] },
      { op: 'type', param: 'query' },
    ],
    tier: 'reversible', cues: ['search', 'find', 'look'],
    reply: 'Searching your reminders for {query}.',
  },
  // The research marks it front-only: "this" is the selection they see. With its pair a press is a flag only;
  // drill that the title flips to Unflag, or it is one toggle and stays held. "unflag this" is not its ask.
  // R3c: a flag on a reminder in a shared list is seen by everyone on it, so the press waits for the yes.
  {
    id: 'reminders.flag_selected', app: REMINDERS, status: 'held',
    title: 'Flag the selected reminder',
    description: 'The selected reminder, after their yes. For asks like "flag this" or "flag this one".',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'menu', path: ['Edit', 'Flag'], pair: ['Flag', 'Unflag'] }],
    tier: 'irreversible', cues: ['flag', 'flagged', 'flag this', 'flag this one', 'mark this flagged', 'isko flag'],
    reply: 'Flag the selected reminder in Reminders?',
  },
  // ── R3c: the catalog rulings' accepts ─────────────────────────────────────────────────────────
  // View only, on the list on screen. Each title flips, so each press is directional; "hide" names the hide.
  opening(menu('reminders.show_completed', 'Show completed reminders', 'In the list on screen. For asks like "show completed reminders" or "uh show checked off items".', ['View', 'Show Completed'], ['completed', 'done', 'finished', 'checked', 'done ones', 'checked off items'], 'Showing completed reminders.', 'Hide Completed')),
  menu('reminders.hide_completed', 'Hide completed reminders', 'In the list on screen. For asks like "hide completed" or "hide the done ones".', ['View', 'Hide Completed'], ['hide', 'chhupa', 'chhupao', 'hide completed', 'hide the completed', 'hide the done ones', 'hide finished', 'complete wale chhupa'], 'Hid completed reminders.', 'Show Completed'),
  opening(menu('reminders.show_sidebar', 'Show the Reminders sidebar', 'The lists sidebar. For asks like "show my lists sidebar" or "bring back the sidebar".', ['View', 'Show Sidebar'], ['sidebar', 'lists', 'lists sidebar', 'back the sidebar', 'sidebar dikhao'], 'Showed the sidebar in Reminders.', 'Hide Sidebar')),
  menu('reminders.hide_sidebar', 'Hide the Reminders sidebar', 'The lists sidebar. For asks like "hide the sidebar in Reminders" or "hide the lists".', ['View', 'Hide Sidebar'], ['hide', 'collapse', 'hatao', 'chhupao', 'hide the sidebar', 'hide sidebar', 'hide the lists', 'collapse the sidebar', 'collapse the reminders sidebar', 'sidebar hatao'], 'Hid the sidebar in Reminders.', 'Show Sidebar'),
  // View menu radio items; a smart list may offer no columns, and then the press refuses.
  opening(menu('reminders.list_view', 'Show the list as a list', 'The list on screen. For asks like "list view in Reminders" or "switch Reminders to list view".', ['View', 'as List'], ['list', 'list view', 'as a list'], 'Showing it as a list.')),
  opening(menu('reminders.columns_view', 'Show the list as columns', 'The list on screen, as a board. For asks like "show this as columns" or "kanban view please".', ['View', 'as Columns'], ['columns', 'column', 'kanban', 'board', 'column view', 'kanban view', 'board view', 'as columns', 'this as columns', 'make it columns', 'columns mein dikhao'], 'Showing it as columns.')),
];
